import { types } from 'mobx-state-tree';
import type { Instance } from 'mobx-state-tree/dist/core/type/type';

import {
  ApplicationGroup,
  ApplicationRating,
  ApplicationStatus,
} from '@/enums/applications';
import { AcademicYear } from '@/enums/users';
import { ScheduledInterviewModel } from '@/models/calendar';
import { UniversityModel } from '@/models/common';
import { ProjectModel, ProjectProblemModel } from '@/models/projects';
import { UserModel } from '@/models/users';
import { edges, nullOrUndefined } from '@/models/utils';
import { BaseModel } from '@/stores/shared';

export const AnswerModel = types.compose(
  BaseModel,
  types.model({
    answerValue: types.maybe(types.frozen()),
    score: types.maybeNull(types.number),
    problem: types.late(() => types.maybe(ProjectProblemModel)),
    application: types.late(() => types.maybe(ApplicationModel)),
  }),
);

export type AnswerModelType = Instance<typeof AnswerModel> & {};

export const ApplicationModel = types.compose(
  BaseModel,
  types.model({
    status: types.maybe(
      types.enumeration<ApplicationStatus>(Object.values(ApplicationStatus)),
    ),
    deletable: types.maybe(types.boolean),
    checked: types.maybe(types.boolean),
    score: types.maybe(types.number),
    maxScore: types.maybe(types.number),
    startedAt: nullOrUndefined(types.string),
    submittedAt: nullOrUndefined(types.string),
    project: types.late(() => types.maybe(ProjectModel)),
    statusUpdated: types.maybe(types.string),
    tasks: types.late(() => types.maybe(edges(ProjectProblemModel))),
    answers: types.maybe(edges(AnswerModel)),
    candidate: types.maybe(UserModel),
    rating: nullOrUndefined(
      types.enumeration<ApplicationRating>(Object.values(ApplicationRating)),
    ),
    mentorComment: nullOrUndefined(types.frozen()),
    interview: types.late(() => nullOrUndefined(ScheduledInterviewModel)),
  }),
);

export type ApplicationModelType = Instance<typeof ApplicationModel>;

export type MentorApplicationModelType = ApplicationModelType & {};

export const ApplicationCountsModel = types.model({
  [ApplicationGroup.UNREVIEWED]: types.number,
  [ApplicationGroup.INTERVIEW]: types.number,
  [ApplicationGroup.INTERNSHIP]: types.number,
  [ApplicationGroup.REJECTED]: types.number,
});

export type ApplicationCountsModelType = Instance<
  typeof ApplicationCountsModel
>;

export const MentorFilterDataModel = types.model({
  groupedApplicationCounts: ApplicationCountsModel,
  filteredApplicationCount: types.number,
  universities: types.array(UniversityModel),
  projects: types.late(() => types.array(ProjectModel)),
  academicYears: types.array(
    types.enumeration<string>(Object.keys(AcademicYear)),
  ),
});

export type MentorFilterDataModelType = Instance<typeof MentorFilterDataModel>;
